import type { PixiBillingClient } from "../../billing/pixiBillingClient";
import type {
  BillingCycle,
  CurrencyCode,
  PlanId,
} from "../PlanBillingStep/constants";
import type { PixiAddress } from "../../hooks/usePixiWizardState";
import { pixiAddressToBillingAddress } from "./mappers/checkoutPayload";

/**
 * The subset of the purchase order needed to set the plan and activate the
 * subscription (steps 3–5 of the Pixi purchase). Mirrors the relevant fields of
 * `PurchaseInput`.
 *
 * `currency` is optional so the no-fallback currency guard still applies here:
 * Chargebee locks the currency to the customer, so if it's missing we fail
 * rather than charge a currency the customer never agreed to.
 */
export interface PixiPurchaseOrder {
  /** Plan tier group, e.g. "core" | "pro". */
  planGroup: PlanId;
  /** Billing cycle, "year" | "month". */
  billingCycle: BillingCycle;
  /** The trialist's locked billing currency, resolved upstream. No fallback. */
  currency?: CurrencyCode;
  licenses: number;
  /**
   * Set for the free-device deal: activates the subscription as a PIXI bundle
   * (adds the free device + paid shipping). The backend resolves the shipping
   * cost from its rate table using the address country + service.
   */
  bundle?: {
    shippingAddress: PixiAddress;
    shippingService: string;
  };
  /**
   * Optional PO number applied to invoices (GRWBKK-1704). Unvalidated, so it is
   * applied best-effort AFTER the subscription is active and never fails the
   * purchase. Carried on the order so both the inline and the redirect-resume
   * paths (whose only shared step is this module) apply it identically.
   */
  poNumber?: string;
}

/**
 * The user-facing default text thrown when the billing currency is missing.
 * Kept identical to usePurchase's `pixi_wizard.checkout.error.currency_missing`
 * default. Callers that need the translated string may pass
 * `opts.currencyMissingMessage`; otherwise this default (matching the existing
 * copy) is used. This keeps `finalizePurchase` a pure, React-free module while
 * preserving the exact user-facing error text.
 */
export const CURRENCY_MISSING_MESSAGE =
  "We couldn't confirm your billing currency. Please try again.";

export interface FinalizePurchaseOptions {
  /** The org's billing client. */
  client: PixiBillingClient;
  /** Optional translated override for the currency-missing error text. */
  currencyMissingMessage?: string;
}

/**
 * Steps 3–6 of the Pixi purchase, extracted verbatim as a pure async function:
 *
 * Step 3 — currency guard (no fallback) → build the plan id.
 * Step 4 — set the chosen plan + licences (updateSubscription), unless the
 * subscription is cancelled, where that call would reactivate and bill it.
 * Step 5 — activate the subscription (startSubscription), passing the PIXI
 * bundle for the free-device deal and the selection step 4 skipped.
 * Step 6 — apply the optional PO number (best-effort; never fails the purchase).
 *
 * This module never touches Stripe.
 */
export const finalizePurchase = async (
  order: PixiPurchaseOrder,
  opts: FinalizePurchaseOptions,
): Promise<void> => {
  const { client, currencyMissingMessage } = opts;

  // 3. Build the plan id from the trialist's locked currency (resolved
  // upstream). No fallback: fail rather than charge a currency the customer
  // never agreed to. group/cycle are the user's choice; only currency is
  // constrained by Chargebee.
  if (!order.currency) {
    throw new Error(currencyMissingMessage ?? CURRENCY_MISSING_MESSAGE);
  }
  const planId = `${order.planGroup}-${order.billingCycle}-${order.currency.toLowerCase()}`;

  // 4–5. Set the plan + licences and end the trial. The free-device deal also
  // passes the bundle option + shipping, so billing adds the free device and
  // charges the shipping it resolves from its rate table. Each billing version
  // does this its own way (see `PixiBillingClient.activate`).
  await client.activate({
    planId,
    quantity: order.licenses,
    bundle: order.bundle
      ? {
          shippingAddress: pixiAddressToBillingAddress(
            order.bundle.shippingAddress,
          ),
          shippingService: order.bundle.shippingService,
        }
      : undefined,
  });

  // 6. Apply the optional PO number to the now-active subscription. This is an
  // unvalidated invoice reference, so it runs AFTER the purchase has fully
  // succeeded and is best-effort: a failure here must never report the
  // (already-charged, already-activated) purchase as failed.
  if (order.poNumber?.trim()) {
    await client.updatePoNumber(order.poNumber.trim());
  }
};
